import {Component, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {Subject} from 'rxjs';
import {first, takeUntil} from 'rxjs/operators';
import {AlertService} from '../services';
import {ComponentTitleService} from '../services/componentTitle.service';
import {FuseTranslationLoaderService} from '../../../@fuse/services/translation-loader.service';
import {locale as polish} from './i18n/pl';
import {locale as english} from './i18n/en';
import {TrainingService} from '../services/training.service';
import {Training, TrainingDetails, TrainingLog} from '../models/training';
import deviceTypesData from './../resource/resources/deviceTypes.json';
import {ResourceType} from "../resource/model/resourceType";

@Component({
    templateUrl: './trainingDetails.component.html',
    styleUrls: ['./trainingDetails.component.scss']
})
export class TrainingDetailsComponent implements OnDestroy, OnInit {
    id: string;
    training: Training;
    trainingLogs: TrainingLog[] = [];
    loading: boolean = false;
    logsLoading: boolean = false;
    initialLoadComplete: boolean = false;
    deviceTypes: ResourceType[] = deviceTypesData;

    // Chart data
    powerChartData: any;
    speedChartData: any;
    heartRateChartData: any;
    distanceCaloriesChartData: any;

    // Chart colors
    powerChartColors: any[] = [
        {
            borderColor: '#4CAF50',
            backgroundColor: 'rgba(76, 175, 80, 0.1)',
        }
    ];
    speedChartColors: any[] = [
        {
            borderColor: '#2196F3',
            backgroundColor: 'rgba(33, 150, 243, 0.1)',
        }
    ];
    heartRateChartColors: any[] = [
        {
            borderColor: '#FF5722',
            backgroundColor: 'rgba(255, 87, 34, 0.1)',
        }
    ];
    distanceCaloriesChartColors: any[] = [
        {
            borderColor: '#FF9800',
            backgroundColor: 'rgba(255, 152, 0, 0.1)',
        },
        {
            borderColor: '#F44336',
            backgroundColor: 'rgba(244, 67, 54, 0.1)',
        }
    ];

    // Chart options
    lineChartOptions: any = {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
            xAxes: [{
                display: true,
                scaleLabel: {
                    display: true,
                    labelString: 'Time'
                }
            }],
            yAxes: [{
                display: true,
                ticks: {
                    beginAtZero: true
                }
            }]
        },
        elements: {
            line: {
                tension: 0.4
            }
        }
    };

    // Dual axis chart options for Distance & Calories
    dualAxisChartOptions: any = {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
            xAxes: [{
                display: true,
                scaleLabel: {
                    display: true,
                    labelString: 'Time'
                }
            }],
            yAxes: [
                {
                    id: 'y-axis-1',
                    type: 'linear',
                    display: true,
                    position: 'left',
                    ticks: {
                        beginAtZero: true
                    },
                    scaleLabel: {
                        display: true,
                        labelString: 'Distance (km)'
                    }
                },
                {
                    id: 'y-axis-2',
                    type: 'linear',
                    display: true,
                    position: 'right',
                    ticks: {
                        beginAtZero: true
                    },
                    scaleLabel: {
                        display: true,
                        labelString: 'Calories'
                    },
                    gridLines: {
                        drawOnChartArea: false
                    }
                }
            ]
        },
        elements: {
            line: {
                tension: 0.4
            }
        }
    };

    private _unsubscribeAll: Subject<any>;
    private refreshInterval: any;
    private autoRefreshEnabled: boolean = true;

    constructor(
        private route: ActivatedRoute,
        private alertService: AlertService,
        private componentTitleService: ComponentTitleService,
        private translationLoader: FuseTranslationLoaderService,
        private trainingService: TrainingService
    ) {
        this._unsubscribeAll = new Subject();
        componentTitleService.clearTitle();
        componentTitleService.addPart('Training Details');
        this.translationLoader.loadTranslations(polish, english);
    }

    ngOnInit() {
        this.id = this.route.snapshot.params['id'];
        this.loadTrainingDetails();
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
        this.stopAutoRefresh();
    }

    startAutoRefresh(): void {
        // Clear any existing interval
        this.stopAutoRefresh();

        // Start new interval - refresh every 5 seconds
        this.refreshInterval = setInterval(() => {
            if (this.autoRefreshEnabled) {
                this.loadTrainingDetails();
            }
        }, 5000);
    }

    stopAutoRefresh(): void {
        if (this.refreshInterval) {
            clearInterval(this.refreshInterval);
            this.refreshInterval = null;
        }
    }

    checkIfShouldAutoRefresh(): void {
        if (!this.trainingLogs || this.trainingLogs.length === 0) {
            this.autoRefreshEnabled = false;
            this.stopAutoRefresh();
            return;
        }

        // Get the most recent log
        const sortedLogs = [...this.trainingLogs].sort((a, b) => {
            return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
        });

        const lastLog = sortedLogs[0];
        const lastLogTime = new Date(lastLog.timestamp).getTime();
        const currentTime = new Date().getTime();
        const timeDifferenceInSeconds = (currentTime - lastLogTime) / 1000;

        // If last log is older than 1 minute (60 seconds), stop auto-refresh
        if (timeDifferenceInSeconds > 60) {
            this.autoRefreshEnabled = false;
            this.stopAutoRefresh();
        } else {
            this.autoRefreshEnabled = true;
        }
    }

    loadTrainingDetails(): void {
        // Only show loading spinner on initial load
        if (!this.initialLoadComplete) {
            this.loading = true;
        }

        this.trainingService.getTrainingDetails(this.id).pipe(first())
            .subscribe((details: TrainingDetails) => {
                    this.training = details.stats;
                    this.trainingLogs = details.logs;
                    this.loading = false;
                    this.initialLoadComplete = true;

                    // Calculate metrics from logs
                    this.calculateMetricsFromLogs();

                    // Prepare chart data
                    this.prepareChartData();

                    // Check if should continue auto-refresh
                    this.checkIfShouldAutoRefresh();

                    // Start auto-refresh if not already started and if enabled
                    if (this.autoRefreshEnabled && !this.refreshInterval) {
                        this.startAutoRefresh();
                    }

                    // Update title with user info (only on first load)
                    if (this.training.user) {
                        const titleParts = this.componentTitleService.getTitleParts();
                        const userFullName = this.training.user.firstName + ' ' + this.training.user.lastName;
                        if (!titleParts.includes(userFullName)) {
                            this.componentTitleService.addPart(userFullName);
                        }
                    }

                    for (let j = 0; j < this.deviceTypes.length; j++) {
                        if (this.deviceTypes[j].id !== this.training.resource.type) {
                            continue;
                        }

                        this.training.resource.image = this.deviceTypes[j].image;
                        break;
                    }
                },
                error => {
                    this.alertService.error(error);
                    this.loading = false;
                });
    }

    calculateMetricsFromLogs(): void {
        if (!this.trainingLogs || this.trainingLogs.length === 0) {
            return;
        }

        // Find earliest and latest timestamps
        let minTimestamp: Date = null;
        let maxTimestamp: Date = null;

        this.trainingLogs.forEach(log => {
            if (log.timestamp) {
                const logTime = new Date(log.timestamp);
                if (!minTimestamp || logTime < minTimestamp) {
                    minTimestamp = logTime;
                }
                if (!maxTimestamp || logTime > maxTimestamp) {
                    maxTimestamp = logTime;
                }
            }
        });

        // Get timeLeft from the most recent log
        const sortedLogs = [...this.trainingLogs].sort((a, b) => {
            return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
        });
        if (sortedLogs.length > 0 && sortedLogs[0].data.timeLeft !== undefined) {
            this.training.timeLeft = sortedLogs[0].data.timeLeft;
        }

        // Calculate duration from timestamps
        if (minTimestamp && maxTimestamp) {
            const durationInSeconds = Math.floor((maxTimestamp.getTime() - minTimestamp.getTime()) / 1000);

            // Update timestamps
            if (!this.training.timestamps) {
                this.training.timestamps = {
                    startedAt: minTimestamp.toISOString(),
                    finishedAt: maxTimestamp.toISOString(),
                    duration: durationInSeconds
                };
            } else {
                this.training.timestamps.startedAt = minTimestamp.toISOString();
                this.training.timestamps.finishedAt = maxTimestamp.toISOString();
                this.training.timestamps.duration = durationInSeconds;
            }
        }

        // Calculate metrics from logs
        let totalPower = 0;
        let totalSpeed = 0;
        let totalHeartRate = 0;
        let maxPower = 0;
        let maxSpeed = 0;
        let maxHeartRate = 0;
        let totalDistance = 0;
        let totalCalories = 0;
        let powerCount = 0;
        let speedCount = 0;
        let heartRateCount = 0;

        this.trainingLogs.forEach(log => {
            if (log.data.power) {
                totalPower += log.data.power;
                powerCount++;
                maxPower = Math.max(maxPower, log.data.power);
            }
            if (log.data.speed) {
                totalSpeed += log.data.speed;
                speedCount++;
                maxSpeed = Math.max(maxSpeed, log.data.speed);
            }
            if (log.data.pulse) {
                totalHeartRate += log.data.pulse;
                heartRateCount++;
                maxHeartRate = Math.max(maxHeartRate, log.data.pulse);
            }
            if (log.data.distance) {
                totalDistance = Math.max(totalDistance, log.data.distance);
            }
            if (log.data.calories) {
                totalCalories = Math.max(totalCalories, log.data.calories);
            }
        });

        // Update metrics
        if (!this.training.metrics) {
            this.training.metrics = {
                totalPower: totalPower,
                avgPower: powerCount > 0 ? Math.round(totalPower / powerCount) : 0,
                maxPower: maxPower,
                avgSpeed: speedCount > 0 ? parseFloat((totalSpeed / speedCount).toFixed(1)) : 0,
                maxSpeed: maxSpeed,
                avgHeartRate: heartRateCount > 0 ? Math.round(totalHeartRate / heartRateCount) : 0,
                maxHeartRate: maxHeartRate,
                totalDistance: totalDistance,
                totalCalories: totalCalories
            };
        } else {
            this.training.metrics.totalPower = totalPower;
            this.training.metrics.avgPower = powerCount > 0 ? Math.round(totalPower / powerCount) : 0;
            this.training.metrics.maxPower = maxPower;
            this.training.metrics.avgSpeed = speedCount > 0 ? parseFloat((totalSpeed / speedCount).toFixed(1)) : 0;
            this.training.metrics.maxSpeed = maxSpeed;
            this.training.metrics.avgHeartRate = heartRateCount > 0 ? Math.round(totalHeartRate / heartRateCount) : 0;
            this.training.metrics.maxHeartRate = maxHeartRate;
            this.training.metrics.totalDistance = totalDistance;
            this.training.metrics.totalCalories = totalCalories;
        }

        // Update logs count
        this.training.logsCount = this.trainingLogs.length;
    }

    prepareChartData(): void {
        if (!this.trainingLogs || this.trainingLogs.length === 0) {
            return;
        }

        // Sort logs by timestamp
        const sortedLogs = [...this.trainingLogs].sort((a, b) => {
            return new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime();
        });

        // Prepare labels and data arrays
        const labels: string[] = [];
        const powerData: number[] = [];
        const speedData: number[] = [];
        const heartRateData: number[] = [];
        const caloriesData: number[] = [];
        const distanceData: number[] = [];

        // Get the start time from the first log
        const startTime = new Date(sortedLogs[0].timestamp).getTime();

        sortedLogs.forEach((log, index) => {
            // Calculate elapsed time from start in seconds
            const logTime = new Date(log.timestamp).getTime();
            const elapsedSeconds = Math.floor((logTime - startTime) / 1000);
            const minutes = Math.floor(elapsedSeconds / 60);
            const seconds = elapsedSeconds % 60;
            labels.push(`${minutes}:${seconds.toString().padStart(2, '0')}`);

            powerData.push(log.data.power || 0);
            speedData.push((log.data.speed || 0) / 10);
            heartRateData.push(log.data.pulse || 0);
            caloriesData.push(log.data.calories || 0);
            distanceData.push((log.data.distance || 0) / 100000); // Convert to km
        });

        // Update existing charts instead of recreating them
        // Power Chart
        if (!this.powerChartData) {
            this.powerChartData = {
                labels: labels,
                datasets: [{
                    label: 'Power (W)',
                    data: powerData,
                    fill: true
                }]
            };
        } else {
            this.powerChartData.labels = labels;
            this.powerChartData.datasets[0].data = powerData;
        }

        // Speed Chart
        if (!this.speedChartData) {
            this.speedChartData = {
                labels: labels,
                datasets: [{
                    label: 'Speed (km/h)',
                    data: speedData,
                    fill: true
                }]
            };
        } else {
            this.speedChartData.labels = labels;
            this.speedChartData.datasets[0].data = speedData;
        }

        // Heart Rate Chart
        if (!this.heartRateChartData) {
            this.heartRateChartData = {
                labels: labels,
                datasets: [{
                    label: 'Heart Rate (bpm)',
                    data: heartRateData,
                    fill: true
                }]
            };
        } else {
            this.heartRateChartData.labels = labels;
            this.heartRateChartData.datasets[0].data = heartRateData;
        }

        // Distance and Calories Chart
        if (!this.distanceCaloriesChartData) {
            this.distanceCaloriesChartData = {
                labels: labels,
                datasets: [
                    {
                        label: 'Distance (km)',
                        data: distanceData,
                        fill: true,
                        yAxisID: 'y-axis-1'
                    },
                    {
                        label: 'Calories',
                        data: caloriesData,
                        fill: true,
                        yAxisID: 'y-axis-2'
                    }
                ]
            };
        } else {
            this.distanceCaloriesChartData.labels = labels;
            this.distanceCaloriesChartData.datasets[0].data = distanceData;
            this.distanceCaloriesChartData.datasets[1].data = caloriesData;
        }
    }

    formatDuration(seconds: number): string {
        if (!seconds) return '-';

        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const secs = seconds % 60;

        if (hours > 0) {
            return `${hours}h ${minutes}m ${secs}s`;
        } else if (minutes > 0) {
            return `${minutes}m ${secs}s`;
        } else {
            return `${secs}s`;
        }
    }

    formatTimeLeft(seconds: number): string {
        if (!seconds || seconds <= 0) return '-';

        const minutes = Math.floor(seconds / 60);
        const secs = seconds % 60;

        return `${minutes}:${secs.toString().padStart(2, '0')}`;
    }
}